"use client";

import { ConvexReactClient } from "convex/react";
import { ReactNode, useEffect } from "react";
import { ConvexBetterAuthProvider } from "@convex-dev/better-auth/react";
import { authClient } from "@/lib/auth-client";
import { getBuildId } from "@/lib/version";
import { CartMigration } from "@/components/cart/CartMigration";
import { installConvexErrorReporter } from "@/lib/utils/clientErrorReporter";

// Use a fallback URL during build time to prevent errors
// The actual URL will be used at runtime
const convexUrl = process.env.NEXT_PUBLIC_CONVEX_URL || "https://placeholder.convex.cloud";

// Create Convex client with credentials included for cookie-based auth
const convex = new ConvexReactClient(convexUrl, {
  // Ensure cookies are sent with every request
  unsavedChangesWarning: false,
});
// A call the server refuses before it runs (an unknown argument, a missing
// function, an auth failure) can never log itself — the client does it,
// so Platform Admin → Errors sees every failure the app hits.
installConvexErrorReporter(convex);

/**
 * Version Checker Component
 * 
 * Monitors for version mismatch errors and triggers automatic reload
 */
function VersionChecker() {
  useEffect(() => {
    // Store current build ID in localStorage
    const currentBuildId = getBuildId();
    const storedBuildId = localStorage.getItem("app_build_id");

    if (!storedBuildId) {
      // First visit - store build ID
      localStorage.setItem("app_build_id", currentBuildId);
    } else if (storedBuildId !== currentBuildId) {
      // Build ID changed - clear old build ID and reload
      console.log("[VersionChecker] Build ID changed:", {
        stored: storedBuildId,
        current: currentBuildId,
      });
      localStorage.setItem("app_build_id", currentBuildId);
      
      // Don't reload immediately on mount - let the app load first
      // The error boundary will handle actual errors
    }

    // Listen for Convex errors
    const handleConvexError = (event: ErrorEvent) => {
      const error = event.error;
      if (!error) return;

      const errorMessage = error.message?.toLowerCase() || "";
      
      // Check for version mismatch patterns
      const isVersionMismatch =
        errorMessage.includes("could not find public function") ||
        errorMessage.includes("could not find function") ||
        errorMessage.includes("function signature mismatch") ||
        errorMessage.includes("invalid argument") ||
        errorMessage.includes("unexpected argument");

      if (isVersionMismatch) {
        console.log("[VersionChecker] Version mismatch detected in Convex error");
        console.log("[VersionChecker] Error:", errorMessage);
        console.log("[VersionChecker] Current build ID:", currentBuildId);
        
        // Let the error boundary handle the reload
        // We just log for debugging purposes
      }
    };

    window.addEventListener("error", handleConvexError);

    return () => {
      window.removeEventListener("error", handleConvexError);
    };
  }, []);

  return null;
}

export function ConvexClientProvider({
  children,
  initialToken,
}: {
  children: ReactNode;
  initialToken?: string | null;
}) {
  return (
    <ConvexBetterAuthProvider
      client={convex}
      authClient={authClient}
      initialToken={initialToken}
    >
      <VersionChecker />
      <CartMigration />
      {children}
    </ConvexBetterAuthProvider>
  );
}
